import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import LogService, { LOG_ACTIONS } from '../services/LogService';

const PLANS = [
  {
    id: 'gratis',
    name: 'Gratuito',
    price: 'R$ 0',
    period: '',
    tag: null,
    highlight: false,
    cta: 'Começar grátis',
    ctaStyle: 'border-2 border-border text-foreground hover:bg-muted',
    features: [
      { label: 'Resultado parcial (2 cômodos)', included: true },
      { label: '1 orçamento', included: true },
      { label: 'Exportar PDF', included: false },
      { label: 'Exportar Excel', included: false },
    ],
  },
  {
    id: 'basico',
    name: 'Básico',
    price: 'R$ 49,90',
    period: '/mês',
    tag: null,
    highlight: false,
    cta: 'Assinar Básico',
    ctaStyle: 'border-2 border-secondary text-secondary hover:bg-secondary/10',
    features: [
      { label: 'Orçamento completo', included: true },
      { label: '10 orçamentos/mês', included: true },
      { label: 'Exportar PDF', included: true },
      { label: 'Exportar Excel', included: true },
    ],
  },
  {
    id: 'pro',
    name: 'Profissional',
    price: 'R$ 99,90',
    period: '/mês',
    tag: 'Mais Popular',
    highlight: true,
    cta: 'Assinar Profissional',
    ctaStyle: 'bg-gradient-cta text-white hover:opacity-90 shadow-cta',
    features: [
      { label: 'Tudo ilimitado', included: true },
      { label: 'Orçamentos ilimitados', included: true },
      { label: 'Exportar PDF e Excel', included: true },
      { label: 'Histórico completo', included: true },
    ],
  },
];

function EscolherPlano() {
  const navigate = useNavigate();
  const { user, updatePlan } = useAuth();
  const [saving, setSaving] = useState(false);

  const handleSelect = async (plan) => {
    LogService.log(LOG_ACTIONS.PAYMENT_ATTEMPT, {
      status: 'info',
      details: { plan: plan.id, planName: plan.name, price: plan.price },
    });

    if (plan.id === 'gratis') {
      setSaving(true);
      try {
        // Get the gratis plan ID from the planos table
        const { data: planoData } = await supabase
          .from('planos')
          .select('id')
          .eq('slug', 'gratis')
          .single();

        if (planoData && user?.id) {
          // Save to usuarios_planos table
          await supabase.from('usuarios_planos').insert({
            usuario_id: user.id,
            plano_id: planoData.id,
            status: 'ativo',
            data_inicio: new Date().toISOString(),
          });
        }
      } catch {
        // Continue even if DB insert fails — metadata update below is the fallback
      }

      // Update user_metadata (always works as primary source)
      updatePlan('gratis');

      // Redirect to home page
      navigate('/', { replace: true });
    } else {
      navigate(`/checkout/${plan.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex flex-col">
      {/* Logo — no link, user must choose a plan */}
      <div className="py-6 text-center">
        <div className="inline-flex items-center gap-3 justify-center">
          <div className="w-10 h-10 rounded-lg bg-gradient-cta flex items-center justify-center">
            <span className="text-white font-bold text-sm">QCO</span>
          </div>
          <span className="font-display font-bold text-2xl text-secondary">QuantoCustaObra</span>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 pb-12">
        <div className="text-center mb-8">
          <h1 className="text-2xl md:text-3xl font-bold font-display text-foreground mb-2">
            Escolha seu plano para começar
          </h1>
          <p className="text-muted-foreground text-sm max-w-xl mx-auto">
            {user?.name
              ? `Olá, ${user.name.split(' ')[0]}! Selecione o plano ideal para seus projetos.`
              : 'Comece grátis ou escolha um plano com mais recursos.'}
          </p>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-3 gap-4 items-stretch max-w-4xl w-full">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`
                relative flex flex-col rounded-2xl border-2 p-5 transition-all duration-200
                ${plan.highlight
                  ? 'border-primary bg-card shadow-elevated md:scale-105 z-10'
                  : 'border-border bg-card hover:border-primary/50 hover:shadow-elevated'
                }
              `}
            >
              {plan.tag && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-cta text-white text-xs font-bold px-4 py-1 rounded-full shadow-md">
                    {plan.tag}
                  </span>
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-lg font-bold font-display text-foreground">{plan.name}</h3>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-foreground">{plan.price}</span>
                  {plan.period && <span className="text-muted-foreground text-sm font-medium">{plan.period}</span>}
                </div>
              </div>

              <ul className="space-y-2 flex-1 mb-5">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    {feature.included ? (
                      <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <span className={`text-sm ${feature.included ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {feature.label}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelect(plan)}
                disabled={saving}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed ${plan.ctaStyle}`}
              >
                {saving && plan.id === 'gratis' ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Salvando...
                  </span>
                ) : plan.cta}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-muted-foreground text-xs mt-6">
          Cancele a qualquer momento. Sem compromisso.
        </p>
      </main>
    </div>
  );
}

export default EscolherPlano;
